import React, { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Body1, Body2, Paper } from "@activecollab/components";
import PropTypes from "prop-types";
import NotFoundSlate from "~/components/EmptySlate/NotFoundSlate";
import { useGlobalPrivateChannelListener } from "~/components/Hooks/useGlobalPrivateChannelListener";
import { Page } from "~/components/Page";
import { Resolver } from "~/components/Resolver.jsx";
import { TrashSlate } from "~/components/TrashSlate/TrashSlate";
import { fetchDiscussion } from "~/store/actions/actions.discussions";
import { fetchObjectLastVisitTimestamp } from "~/store/actions/actions.lastVisit";
import { fetchProject } from "~/store/actions/actions.projects";
import { getDiscussionById } from "~/store/selectors/selector.discussions";
import { getLoggedUser } from "~/store/selectors/selector.users";
import { DiscussionRealTime } from "./DiscussionRealTime";
import { useDiscussionActions } from "./useDiscussionActions";

const NotFound = () => {
  const { t } = useTranslation();
  return (
    <Paper className="tw-flex tw-flex-col tw-h-full tw-mb-8">
      <NotFoundSlate
        header={t("This discussion is not found")}
        pageName={t("projects page")}
        pageLink="/projects"
      />
    </Paper>
  );
};

export const DiscussionTrashSlate = ({ id, projectId, children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const discussion = useSelector((state) => getDiscussionById(state, id));
  const [notFoundShow, setNotFoundShow] = useState(() => {
    if (discussion) {
      return discussion.project_id !== projectId; //case when you go back in browser and discussion is moved to another project
    }
    return false;
  });
  const resolveDependencies = useCallback(async () => {
    await dispatch(fetchObjectLastVisitTimestamp("Discussion", id));
    await dispatch(fetchProject(projectId));
    await dispatch(fetchDiscussion(projectId, id)).then(({ error }) => {
      if (error) {
        throw error;
      }
    });
  }, [dispatch, id, projectId]);

  const { handleRestore, handleDelete, canRestore } = useDiscussionActions(id, projectId);

  const loggedUser = useSelector(getLoggedUser);

  const type = "Discussion";

  useGlobalPrivateChannelListener("DataObjectDeleted", (data) => {
    if (data.type === type && data.id === id) {
      setNotFoundShow(true);
    }
  });

  if (notFoundShow) {
    return <NotFound />;
  }

  return (
    <Resolver
      key={`discussion-${id}-${projectId}`}
      callback={resolveDependencies}
      loadingComponent={<Page.Loader />}
      errorComponent={<NotFound />}
    >
      <TrashSlate
        type={type}
        className="tw-h-full tw-items-center"
        subtitle={
          <>
            <Body1
              align="center"
              color="primary"
              className="tw-text-center tw-text-theme-900"
            >
              <Trans>This {type.toLowerCase()} is deleted</Trans>.
            </Body1>

            {canRestore ? (
              <Body2
                color="primary"
                align="center"
                className="tw-mt-2"
              >
                {t("If you want to work on it again, recover it")}.
              </Body2>
            ) : null}
          </>
        }
        isTrashed={discussion?.is_trashed}
        trashedOn={discussion?.trashed_on}
        trashedById={discussion?.trashed_by_id}
        createdById={discussion?.created_by_id}
        createdByName={discussion?.created_by_name}
        createdOn={discussion?.created_on}
        canRestore={canRestore}
        canPermanentlyDelete={loggedUser.class === "Owner"}
        onRestore={handleRestore}
        onPermanentlyDelete={handleDelete}
      >
        {children}
      </TrashSlate>
      <DiscussionRealTime
        id={id}
        projectId={projectId}
      />
    </Resolver>
  );
};

DiscussionTrashSlate.propTypes = {
  id: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  children: PropTypes.node,
};
